import { formatPhone } from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote, Modal } from "../../components/ui";
import { t, tn } from "../../i18n";
import { navigate } from "../../lib/hooks";
import { leadFromUnknownNumber, type UnknownNumber } from "../../lib/queries";
import { useSession } from "../../session";

/**
 * The unclaimed leads, at the head of the New Leads column.
 *
 * Somebody who rang or texted one of the four numbers this morning and is in
 * nobody's records. They used to be listed on Reports, which is a screen a
 * person opens on purpose about once a week — Jonathon, 2026-08-23: "i don't
 * love it on the reports area. I want it to be on mine and lysa's dashboard.
 * I'm think there should be a new lead bucket and it would go right there."
 * A stranger who called this morning is work for today, and work for today
 * belongs on the board that is already open.
 *
 * The bucket is shared. One person claiming a lead takes it off everybody's
 * board, because there is one caller and one job to make from them. Whose board
 * it appears on at all is `profiles.takes_new_leads` — Jonathon, Lysa and
 * Samuel.
 *
 * Nothing is created until somebody presses the button, for the reason in
 * ADR 0058: a stranger's call is as likely to be a robocall as a roof.
 */
export function NewLeadBucket({
  rows,
  onClaimed,
}: {
  rows: readonly UnknownNumber[];
  /** Reload the pipeline and the bucket: there is a new job on the board. */
  onClaimed: () => Promise<void> | void;
}) {
  const { profileId } = useSession();
  const [claiming, setClaiming] = useState<UnknownNumber | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function makeLead() {
    if (!claiming) return;
    setBusy(true);
    setError(null);
    try {
      const made = await leadFromUnknownNumber(
        claiming.phone,
        name.trim() || null,
        profileId ?? null,
      );
      setClaiming(null);
      setName("");
      await onClaimed();
      navigate(`job=${made.jobId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {rows.map((row) => (
        <UnclaimedCard
          key={row.phone}
          row={row}
          onPick={() => {
            setClaiming(row);
            setName("");
            setError(null);
          }}
        />
      ))}

      {claiming && (
        <Modal title={t("Make a lead")} onClose={() => setClaiming(null)}>
          <p>
            {t("A new contact and a job at New Lead for")}{" "}
            <b>{formatPhone(claiming.phone)}</b>
            {claiming.lead_source
              ? t(", from {source}", { source: claiming.lead_source })
              : ""}
            .
            {claiming.calls + claiming.texts > 0 &&
              ` ${t("Everything they have already sent moves onto it.")}`}
          </p>
          <label>
            {t("Their name")}
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={formatPhone(claiming.phone) ?? ""}
              autoFocus
            />
          </label>
          {/* Left blank on purpose: a contact called "205-555-0147" is honest,
              and somebody renames it once they have asked. */}
          <div className="na-hint">
            {t("Leave it blank if you do not know it yet.")}
          </div>

          <ErrorNote error={error} />

          <div className="actions">
            <button
              className="btn-ghost"
              disabled={busy}
              onClick={() => setClaiming(null)}
            >
              {t("Cancel")}
            </button>
            <button
              className="btn-primary"
              disabled={busy}
              // Fire and forget: makeLead() shows its own errors.
              onClick={() => void makeLead()}
            >
              {busy ? t("Making…") : t("Make the lead")}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

/**
 * One stranger, as a card in the column.
 *
 * Deliberately not shaped like a job card: it has no value, no bid type and no
 * follow-up clock, because none of those exist until somebody decides this is a
 * lead. What it has instead is the thing that decides it — what they actually
 * said.
 */
function UnclaimedCard({
  row,
  onPick,
}: {
  row: UnknownNumber;
  onPick: () => void;
}) {
  const did = [
    row.calls > 0 ? tn(row.calls, "1 call", "{n} calls") : null,
    row.voicemails > 0
      ? tn(row.voicemails, "1 voicemail", "{n} voicemails")
      : null,
    row.texts > 0 ? tn(row.texts, "1 text", "{n} texts") : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className="pcard unclaimed"
      onClick={onPick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onPick();
      }}
    >
      <div className="callbar new">
        <span>{t("New — nobody we know")}</span>
        <span className="since">{row.lead_source ?? t("No source")}</span>
      </div>
      <div className="pcard-body">
        <div className="pcard-top">
          <div className="cn">{formatPhone(row.phone)}</div>
        </div>
        {/* What they said, because that is the whole decision. */}
        {row.last_text && <div className="ca">“{row.last_text}”</div>}
        <div className="pcard-foot">
          <span className="pill">{did}</span>
        </div>
      </div>
    </div>
  );
}
